import { defineStore } from "pinia"
import { computed, ref } from "vue"

import {
  Company,
  CompanyMemberCensusUploadAuditLog,
  CompanyMemberTypeEnum,
  UserTypeEnum
} from "@vitable/asclepius-api-client-ts"

// @ts-ignore
import { useGetCompany, useGetEmployerCompany } from "@/composables/company"
import { useListCompanyPeople } from "@/modules/member-management/composables/member"
import { useListCompanyMemberCensusUploadAuditLogs } from "@common/composables/company-member-census"
import { isEmbeddedContext } from "@common/services/embed-service"

const ACTIVE_COMPANY_SESSION_KEY = "gaia-active-company-id"

export type CompanyContextStatus = "initializing" | "needs-employer" | "ready" | "switching" | "error"
export type CompanyContextToken = {
  companyId: string
  generation: number
}

export const useCompanyStore = defineStore("companyStore", () => {
  // State
  const company = ref<Nullable<Company>>(null)
  const activeCompanyId = ref<Nullable<string>>(null)
  const contextStatus = ref<CompanyContextStatus>("initializing")
  const contextError = ref<Nullable<string>>(null)
  const employeesWithCriticalMissingInfoCount = ref<number>(0)
  const employeeCensusUploadedInLast90DaysIn = ref<Nullable<boolean>>(null)
  const lastEmployeeCensusUploadedDate = ref<Nullable<Date>>(null)
  const lastEmployeeCensusUploadedDateInitialized = ref(false)
  const localMoovBankAccountId = ref<Nullable<string>>(null)
  let companyContextGeneration = 0

  // Getters
  const companyName = computed(() => company.value?.name ?? null)
  const companyId = computed(() => activeCompanyId.value)
  const publicCompanyId = computed(() => company.value?.public_company_id ?? null)
  const employerId = computed(() => company.value?.employer_id ?? null)
  const allowedToUpgradePlanIn = computed(() => !!company.value?.allow_auto_plan_upgrade_in)
  const companyOnboardedIn = computed(() => !!company.value?.onboarded_in)
  const paymentProviderId = computed<Nullable<string>>(() => company.value?.moov_payment_provider_id ?? null)
  const isApiPlatformLinked = computed(() => !!company.value?.is_api_platform_linked)

  // Reactive Data State
  const { getCompany: _fetchCompany, company: _fetchedCompany, getCompanyResult: _getCompanyResult } = useGetCompany()
  const {
    getEmployerCompany: _fetchEmployerCompany,
    company: _fetchedEmployerCompany,
    getEmployerCompanyResult: _getEmployerCompanyResult
  } = useGetEmployerCompany()

  const getCompanyResult = isEmbeddedContext() ? _getEmployerCompanyResult : _getCompanyResult

  // Actions
  function $reset() {
    invalidateCompanyContext()
    company.value = null
    activeCompanyId.value = null
    contextStatus.value = "initializing"
    contextError.value = null
    employeesWithCriticalMissingInfoCount.value = 0
    employeeCensusUploadedInLast90DaysIn.value = null
    lastEmployeeCensusUploadedDate.value = null
    lastEmployeeCensusUploadedDateInitialized.value = false
    localMoovBankAccountId.value = null
    window.sessionStorage.removeItem(ACTIVE_COMPANY_SESSION_KEY)
  }

  function initializeActiveCompanyId(
    userType: Optional<UserTypeEnum>,
    companyAdminCompanyId?: string | null,
    vitableAdminCompanyId?: string | null
  ) {
    invalidateCompanyContext()
    contextError.value = null

    if (userType === UserTypeEnum.VitableAdmin) {
      activeCompanyId.value = vitableAdminCompanyId ?? window.sessionStorage.getItem(ACTIVE_COMPANY_SESSION_KEY)
    } else {
      activeCompanyId.value = companyAdminCompanyId ?? null
    }

    contextStatus.value = activeCompanyId.value ? "initializing" : "needs-employer"
    return activeCompanyId.value
  }

  async function fetchCompany(companyIdToFetch: string): Promise<Company> {
    await _fetchCompany(companyIdToFetch)
    const fetchedCompany = _fetchedCompany.value
    if (!fetchedCompany) throw new Error("The selected employer could not be loaded.")
    return fetchedCompany
  }

  async function getCompany(companyIdToFetch = activeCompanyId.value) {
    if (isEmbeddedContext()) {
      await _fetchEmployerCompany()
      const employerCompany = _fetchedEmployerCompany.value
      if (!employerCompany) throw new Error("The employer context could not be loaded.")
      updateCompany(employerCompany)
    } else {
      if (!companyIdToFetch) throw new Error("Choose an employer before loading company data.")
      updateCompany(await fetchCompany(companyIdToFetch))
    }

    contextStatus.value = "ready"
  }

  function updateCompany(newCompany: Nullable<Company>) {
    company.value = newCompany
    activeCompanyId.value = newCompany?.id ?? null
  }

  function commitVitableAdminCompany(newCompany: Company) {
    updateCompany(newCompany)
    window.sessionStorage.setItem(ACTIVE_COMPANY_SESSION_KEY, newCompany.id)
    contextError.value = null
  }

  function clearVitableAdminCompany() {
    invalidateCompanyContext()
    company.value = null
    activeCompanyId.value = null
    contextStatus.value = "needs-employer"
    contextError.value = null
    window.sessionStorage.removeItem(ACTIVE_COMPANY_SESSION_KEY)
    resetCompanyScopedState()
  }

  function resetCompanyScopedState() {
    employeesWithCriticalMissingInfoCount.value = 0
    employeeCensusUploadedInLast90DaysIn.value = null
    lastEmployeeCensusUploadedDate.value = null
    lastEmployeeCensusUploadedDateInitialized.value = false
    localMoovBankAccountId.value = null
  }

  function setContextStatus(status: CompanyContextStatus, error: string | null = null) {
    contextStatus.value = status
    contextError.value = error
  }

  function invalidateCompanyContext() {
    companyContextGeneration++
  }

  function getCompanyContextToken(): CompanyContextToken | null {
    if (!companyId.value) return null
    return { companyId: companyId.value, generation: companyContextGeneration }
  }

  function isCompanyContextCurrent(token: CompanyContextToken): boolean {
    return token.companyId === companyId.value && token.generation === companyContextGeneration
  }

  function requireCompanyId(errorMessage: string): string {
    if (!companyId.value) throw new Error(errorMessage)
    return companyId.value
  }

  function updateMoovBankAccountId(bankAccountId: string) {
    localMoovBankAccountId.value = bankAccountId
  }

  async function loadEmployeesWithMissingInfoCount(contextToken?: CompanyContextToken) {
    const token = contextToken ?? getCompanyContextToken()
    if (!token || !isCompanyContextCurrent(token)) return

    const { listCompanyMembers, members } = useListCompanyPeople()
    await listCompanyMembers({ missingCriticalEmployeeInfoIn: true })
    if (!isCompanyContextCurrent(token)) return
    employeesWithCriticalMissingInfoCount.value = members.totalCount
  }

  async function loadWasEmployeeCensusUploadedInLast90Days(contextToken?: CompanyContextToken) {
    const token = contextToken ?? getCompanyContextToken()
    if (!token || !isCompanyContextCurrent(token)) return

    const ninetyOneDaysAgo = new Date()
    ninetyOneDaysAgo.setDate(ninetyOneDaysAgo.getDate() - 91)

    const { listCompanyMemberCensusUploadAuditLogs } = useListCompanyMemberCensusUploadAuditLogs({
      companyId: token.companyId,
      uploadType: "COMPLETE",
      companyMemberType: CompanyMemberTypeEnum.Employee,
      createdDateGt: ninetyOneDaysAgo.toISOString().split("T")[0]
    })

    const censusUploadLogs: Array<CompanyMemberCensusUploadAuditLog> = (await listCompanyMemberCensusUploadAuditLogs())
      .results

    if (!isCompanyContextCurrent(token)) return
    employeeCensusUploadedInLast90DaysIn.value = censusUploadLogs.length > 0
  }

  async function loadLastEmployeeCensusUploadedDate(contextToken?: CompanyContextToken) {
    const token = contextToken ?? getCompanyContextToken()
    if (!token || !isCompanyContextCurrent(token)) return

    lastEmployeeCensusUploadedDateInitialized.value = false
    try {
      const { listCompanyMemberCensusUploadAuditLogs } = useListCompanyMemberCensusUploadAuditLogs({
        companyId: token.companyId,
        uploadType: "COMPLETE",
        companyMemberType: CompanyMemberTypeEnum.Employee
      })

      const censusUploadLogs: Array<CompanyMemberCensusUploadAuditLog> = (
        await listCompanyMemberCensusUploadAuditLogs()
      ).results
      if (!isCompanyContextCurrent(token)) return
      if (censusUploadLogs.length > 0) {
        lastEmployeeCensusUploadedDate.value = new Date(censusUploadLogs[0].created)
      } else {
        lastEmployeeCensusUploadedDate.value = null
      }
    } finally {
      if (isCompanyContextCurrent(token)) lastEmployeeCensusUploadedDateInitialized.value = true
    }
  }

  return {
    // State
    company,
    activeCompanyId,
    contextStatus,
    contextError,
    getCompanyResult,
    employeesWithCriticalMissingInfoCount,
    employeeCensusUploadedInLast90DaysIn,
    lastEmployeeCensusUploadedDate,
    lastEmployeeCensusUploadedDateInitialized,
    localMoovBankAccountId,
    // Getters
    companyName,
    companyId,
    publicCompanyId,
    employerId,
    allowedToUpgradePlanIn,
    companyOnboardedIn,
    paymentProviderId,
    isApiPlatformLinked,
    // Actions
    $reset,
    initializeActiveCompanyId,
    fetchCompany,
    getCompany,
    updateCompany,
    commitVitableAdminCompany,
    clearVitableAdminCompany,
    resetCompanyScopedState,
    setContextStatus,
    invalidateCompanyContext,
    getCompanyContextToken,
    isCompanyContextCurrent,
    requireCompanyId,
    updateMoovBankAccountId,
    loadEmployeesWithMissingInfoCount,
    loadWasEmployeeCensusUploadedInLast90Days,
    loadLastEmployeeCensusUploadedDate
  }
})
